// Kanemori Sanctuary countryside (M10): the winding mountain road is one smooth centre line in tiles
// (world.sanctuary.road), shared by collision (map.ts turns every tile whose centre lies within `verge` of the line into
// drivable ground) and the 2x art (hd-sanctuary-country.ts paints a smooth asphalt ribbon `half` tiles either side of
// the same line, and the rest of the drivable band as a gravel and grass verge). The verge is wider than the ribbon,
// so a car on the asphalt never meets the tile staircase at the corridor's edge.
import { WORLD, type Rect } from './data';

export interface CountryRoad { name: string; half: number; verge: number; points: [number, number][] }
/** a point on the centre line: tiles, heading (radians), arc length from the start (tiles) */
export interface RoadSample { x: number; y: number; a: number; s: number }

export const COUNTRY_ROAD = (): CountryRoad | undefined => WORLD.sanctuary?.road;

/** Catmull-Rom spline through the points, sampled about every `step` tiles. */
export function sampleRoad(r: CountryRoad, step = 0.25): RoadSample[] {
  const P = r.points, out: RoadSample[] = [];
  const cr = (a: number, b: number, c: number, d: number, t: number) =>
    0.5 * (2 * b + (-a + c) * t + (2 * a - 5 * b + 4 * c - d) * t * t + (-a + 3 * b - 3 * c + d) * t * t * t);
  for (let i = 0; i < P.length - 1; i++) {
    const p0 = P[Math.max(0, i - 1)], p1 = P[i], p2 = P[i + 1], p3 = P[Math.min(P.length - 1, i + 2)];
    const n = Math.max(2, Math.ceil(Math.hypot(p2[0] - p1[0], p2[1] - p1[1]) / step));
    for (let k = 0; k < n; k++) {
      const t = k / n;
      out.push({ x: cr(p0[0], p1[0], p2[0], p3[0], t), y: cr(p0[1], p1[1], p2[1], p3[1], t), a: 0, s: 0 });
    }
  }
  const last = P[P.length - 1]; out.push({ x: last[0], y: last[1], a: 0, s: 0 });
  for (let i = 0; i < out.length; i++) {
    const a = out[Math.max(0, i - 1)], b = out[Math.min(out.length - 1, i + 1)];
    out[i].a = Math.atan2(b.y - a.y, b.x - a.x);
    if (i) out[i].s = out[i - 1].s + Math.hypot(out[i].x - out[i - 1].x, out[i].y - out[i - 1].y);
  }
  return out;
}

const segDist = (px: number, py: number, ax: number, ay: number, bx: number, by: number) => {
  const dx = bx - ax, dy = by - ay, l2 = dx * dx + dy * dy;
  const t = l2 ? Math.max(0, Math.min(1, ((px - ax) * dx + (py - ay) * dy) / l2)) : 0;
  return Math.hypot(px - ax - t * dx, py - ay - t * dy);
};

/** Distance (tiles) from each tile centre to the centre line, for tiles within `reach` of it; Infinity elsewhere.
 *  Index y * w + x. map.ts reads it for collision, the art for the verge and the trees that keep off the road. */
export function roadField(r: CountryRoad, w: number, h: number, reach = r.verge + 4): Float32Array {
  const f = new Float32Array(w * h).fill(Infinity), S = sampleRoad(r);
  for (let i = 0; i < S.length - 1; i++) {
    const a = S[i], b = S[i + 1];
    const x0 = Math.max(0, Math.floor(Math.min(a.x, b.x) - reach)), x1 = Math.min(w - 1, Math.ceil(Math.max(a.x, b.x) + reach));
    const y0 = Math.max(0, Math.floor(Math.min(a.y, b.y) - reach)), y1 = Math.min(h - 1, Math.ceil(Math.max(a.y, b.y) + reach));
    for (let y = y0; y <= y1; y++) for (let x = x0; x <= x1; x++) {
      const d = segDist(x + 0.5, y + 0.5, a.x, a.y, b.x, b.y);
      if (d < f[y * w + x]) f[y * w + x] = d;
    }
  }
  return f;
}

const fields = new Map<string, Float32Array>();
/** the cached field for the shipped road on a w x h map */
export function countryField(w: number, h: number): Float32Array | null {
  const r = COUNTRY_ROAD(); if (!r) return null;
  const k = `${w}x${h}`; let f = fields.get(k);
  if (!f) fields.set(k, f = roadField(r, w, h));
  return f;
}
/** tiles of the drivable corridor (tile centre within `verge` of the centre line) */
export function roadTiles(r: CountryRoad, w: number, h: number): number[] {
  const f = roadField(r, w, h, r.verge + 1), out: number[] = [];
  for (let i = 0; i < f.length; i++) if (f[i] <= r.verge) out.push(i);
  return out;
}

/** Countryside features (world.sanctuary.features): presentation areas on the forest floor; their solid parts (pond
 *  water, the tea house, the shrine) are listed in world.sanctuary.solid / world.lows like any other collision. */
export type FeatureKind = 'garden' | 'onsen' | 'paddies' | 'bamboo' | 'inari' | 'stream' | 'reststop' | 'sakura' | 'meadow' | 'pilgrim' | 'tea';
export interface Feature extends Rect { kind: FeatureKind; name: string; /** a path through it (tiles): the stair, the torii row, the stream, the grove path */ points?: [number, number][] }
export const FEATURES = (): Feature[] => WORLD.sanctuary?.features ?? [];
export const featureAt = (tx: number, ty: number, kind?: FeatureKind) =>
  FEATURES().find(f => (!kind || f.kind === kind) && tx >= f.x && ty >= f.y && tx < f.x + f.w && ty < f.y + f.h);
